/**
 * K线数据服务
 */

import { apiClient } from './apiClient';
import { BarInterval } from '@/services/enums';
import type { KlineData } from './klineTypes';

export class KlineDataService {
  /**
   * 获取K线数据
   */
  async getKlineData(
    symbol: string,
    bar: BarInterval,
    options?: {
      start?: number; // 开始时间戳（秒）
      end?: number; // 结束时间戳（秒）
      limit?: number; // 最大返回数量
    },
  ): Promise<KlineData> {
    const params: any = {
      symbol,
      bar,
    };

    if (options?.start) {
      params.start = options.start.toString();
    }
    if (options?.end) {
      params.end = options.end.toString();
    }
    if (options?.limit) {
      params.limit = options.limit.toString();
    }

    const response = await apiClient.get<KlineData>('/kline', { params });

    if (!response.success) {
      throw new Error(response.message || '获取K线数据失败');
    }

    return response.data;
  }

  /**
   * 获取K线数据的时间范围
   */
  async getKlineTimeRange(
    symbol: string,
    bar: BarInterval,
  ): Promise<{
    hasData: boolean;
    earliestTime: number | null; // 毫秒时间戳
    latestTime: number | null; // 毫秒时间戳
  }> {
    const response = await apiClient.get<{
      hasData: boolean;
      earliestTime: number | null;
      latestTime: number | null;
    }>('/kline/time-range', {
      params: {
        symbol,
        bar,
      },
    });

    if (!response.success) {
      throw new Error(response.message || '获取K线时间范围失败');
    }

    return response.data;
  }

  /**
   * 根据 promptHash 获取提示词内容
   * @param hash promptHash
   * @param bar 可选，时间粒度（如果提供，会同时返回 systemPrompt）
   */
  async getPrompt(
    hash: string,
    bar?: string,
  ): Promise<{
    systemPrompt?: string;
    userPrompt: string;
    hash: string;
  }> {
    const params = bar ? { bar } : {};
    const response = await apiClient.get<{
      systemPrompt?: string;
      userPrompt: string;
      hash: string;
    }>(`/kline/prompt/${hash}`, { params });

    if (!response.success) {
      throw new Error(response.message || '获取提示词失败');
    }

    return response.data;
  }

  /**
   * 根据 analysisReportHash 获取分析报告内容
   */
  async getAnalysisReport(hash: string): Promise<{ analysisReport: string; hash: string }> {
    const response = await apiClient.get<{ analysisReport: string; hash: string }>(`/kline/analysis-report/${hash}`);

    if (!response.success) {
      throw new Error(response.message || '获取分析报告失败');
    }

    return response.data;
  }

  /**
   * 生成AI提示词（基于K线视口时间范围）
   * @returns 返回包含 systemPrompt、userPrompt、symbol、bar、startTime、endTime、promptHash 的完整结构体
   */
  async generateAIPrompt(
    symbol: string,
    bar: BarInterval = '1H',
    options: {
      start: number; // 开始时间戳（秒）
      end: number; // 结束时间戳（秒）
      systemPromptId?: number; // 系统提示词ID（可选）
    },
  ): Promise<{
    systemPrompt: string;
    userPrompt: string;
    symbol: string;
    bar: string;
    startTime: number;
    endTime: number;
    promptHash: string;
  }> {
    const params: any = {
      bar,
      start: options.start.toString(),
      end: options.end.toString(),
    };
    if (options.systemPromptId !== undefined) {
      params.systemPromptId = options.systemPromptId.toString();
    }
    const response = await apiClient.get<{
      systemPrompt: string;
      userPrompt: string;
      symbol: string;
      bar: string;
      startTime: number;
      endTime: number;
      promptHash: string;
    }>(`/kline/generate-ai-prompt/${symbol}`, {
      params,
    });

    if (!response.success) {
      throw new Error(response.message || '生成AI提示词失败');
    }

    return response.data;
  }

  /**
   * 清除AI提示词缓存
   * @param promptHash prompt 的 hash 值
   */
  async clearPromptCache(promptHash: string): Promise<any> {
    const response = await apiClient.delete<any>(`/kline/prompt-cache/${promptHash}`);

    if (!response.success) {
      throw new Error(response.message || '清除提示词缓存失败');
    }

    return response.data;
  }

  /**
   * 获取原始AI响应数据（包括analysisReport和monitorRules）
   */
  async getAIResponse(
    symbol: string,
    bar: BarInterval,
  ): Promise<{
    analysisReport: string;
    monitorRules: any;
  }> {
    const response = await apiClient.get<{
      analysisReport: string;
      monitorRules: any;
    }>(`/kline/ai-response/${symbol}/${bar}`);

    if (!response.success) {
      throw new Error(response.message || '获取AI响应数据失败');
    }

    return response.data;
  }

  /**
   * 更新AI响应数据（更新缓存并调用updateMonitorRulesFromCache）
   */
  async updateAIResponse(
    symbol: string,
    bar: BarInterval,
    data: {
      prompt?: string; // 完整提示词（可选）
      analysisReport?: string; // 分析报告（可选）
      monitorRules: any; // 监控规则（必需）
    },
  ): Promise<any> {
    const response = await apiClient.put<any>(`/kline/ai-response/${symbol}/${bar}`, data);

    if (!response.success) {
      throw new Error(response.message || '更新AI响应数据失败');
    }

    return response.data;
  }

  /**
   * 缓存提示词
   */
  async cachePrompt(promptHash: string, prompt: string): Promise<any> {
    const response = await apiClient.post<any>('/kline/prompt/cache', { prompt, promptHash });

    if (!response.success) {
      throw new Error(response.message || '缓存提示词失败');
    }

    return response.data;
  }

  /**
   * 更新监控任务的提示词hash和时间范围
   */
  async updateMonitorPrompt(
    monitorId: string,
    data: {
      promptHash: string;
      startTime?: number;
      endTime?: number;
    },
  ): Promise<any> {
    const response = await apiClient.put<any>(`/kline/monitor/${monitorId}/prompt`, data);

    if (!response.success) {
      throw new Error(response.message || '更新监控任务失败');
    }

    return response.data;
  }
}

export const klineDataService = new KlineDataService();
